import { z } from 'zod'

export const tagTargetTypeSchema = z.enum(['ACTIVITY', 'RECORD', 'FOLLOWUP'])
export type TagTargetType = z.infer<typeof tagTargetTypeSchema>

export const tagAssignmentModeSchema = z.enum(['OPEN', 'RESTRICTED'])
export type TagAssignmentMode = z.infer<typeof tagAssignmentModeSchema>

export const tagSchema = z.object({
  id: z.string().uuid(),
  targetType: tagTargetTypeSchema,
  label: z.string(),
  colorHex: z.string().nullable(),
  archived: z.boolean(),
  /** Category of the tag, or null if uncategorized. */
  categoryId: z.string().uuid().nullable().optional(),
  /** RESTRICTED tags also need tag:use to be put on things. */
  assignmentMode: tagAssignmentModeSchema.default('OPEN'),
  /** Whether the caller may apply this tag (false only for restricted tags without tag:use). */
  canUse: z.boolean().nullable().optional(),
})

export const createTagRequestSchema = z.object({
  targetType: tagTargetTypeSchema,
  label: z.string().min(1).max(64),
  colorHex: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
  categoryId: z.string().uuid().nullable().optional(),
})

/** Optional grouping of tags. singleSelect: at most one of its tags per record at a time. */
export const tagCategorySchema = z.object({
  id: z.string().uuid(),
  targetType: tagTargetTypeSchema,
  name: z.string().nullable(),
  singleSelect: z.boolean(),
})

export const tagCategoryRequestSchema = z.object({
  targetType: tagTargetTypeSchema,
  name: z.string().max(64).nullable().optional(),
  singleSelect: z.boolean(),
})

export type Tag = z.infer<typeof tagSchema>
export type CreateTagRequest = z.infer<typeof createTagRequestSchema>
export type TagCategory = z.infer<typeof tagCategorySchema>
export type TagCategoryRequest = z.infer<typeof tagCategoryRequestSchema>

/** Display name of a category (categories may be unnamed). */
export function categoryDisplayName(category: Pick<TagCategory, 'name'>): string {
  return category.name?.trim() || 'Unnamed group'
}
